import React from "react";
import { styled } from "@mui/material/styles";
import { Table, TableContainer } from "@mui/material";
import { useHeadroomContext } from "../../context/HeadRoomProvider";
import { useSegmentData } from "../../hooks/useSegmentData";
import { Column } from "../../HeadRoom";
import MDTypography from "components/MDTypography";
import SegmentationTableHeader from "./SegmentationTableHeader";
import SegmentationTableBody from "./SegmentationTableBody";
import TablePaginationWrapper, { MAX_ROWS } from "./TablePaginationWrapper";
import colors from "assets/theme-dark/base/colors";
import { useTableHeaders } from "../../hooks/useSegmentationTableHeaders";
import { useTableSort } from "../../hooks/useTableSort";
import { useTableSegments } from "../../hooks/useTableSegment";
import TableToolbar from "./TableToolbar";
import DownloadIcon from "@mui/icons-material/Download";
import FunctionsIcon from "@mui/icons-material/Functions";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { validateDriversAndSegments } from "../../utils/validationUtils";
import { exportSegmentationDataToExcel } from "../../utils/exportSegmentationDataToExcel";
import { useHeadroomSaveData } from "../../hooks/useHeadroomSaveData";
import { context } from "../../../../../utils/Provider";

interface SegmentationTableProps {
  columns: Column[];
}

const SegmentationTable: React.FC<SegmentationTableProps> = ({ columns }) => {
  const { setIsLoading } = React.useContext(context);
  const {
    segmentGroups,
    driver1Type,
    driver2Type,
    driver1Mode,
    driver2Mode,
    driver1NumeratorType,
    driver2NumeratorType,
    tableName,
  } = useHeadroomContext();

  const {
    data,
    loading,
    error,
    totalCount,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    fetchData,
    hasNextPage,
    hasPreviousPage,
  } = useSegmentData();

  const { selectableHeader1, selectableHeader2, headerOptions, setSelectableHeader1, setSelectableHeader2 } =
    useTableHeaders({
      columns,
    });

  const { saveSegmentationData, segmentationResult } = useHeadroomSaveData();
  const { segments, hasOneDataAndOneManualSegment, isManualOnly } = useTableSegments({ segmentGroups, data });
  const { sortColumn, sortDirection, handleSort } = useTableSort(segments, hasOneDataAndOneManualSegment);

  React.useEffect(() => {
    if (isManualOnly && rowsPerPage !== 100) {
      setRowsPerPage(MAX_ROWS);
      setPage(1);
    }
  }, [isManualOnly, rowsPerPage, setRowsPerPage, setPage]);

  const handlePageChange = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    const maxPage = Math.max(1, Math.ceil(totalCount / rowsPerPage));
    const boundedPage = Math.min(Math.max(1, newPage), maxPage);
    setPage(boundedPage);
    fetchData({ offset: (boundedPage - 1) * rowsPerPage });
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newRowsPerPage = Number(event.target.value);
    setRowsPerPage(newRowsPerPage);
    setPage(1);
    fetchData({ limit: newRowsPerPage, offset: 0 });
  };

  const handleCalculateSegmentData = () => {
    fetchData();
  };

  const handleExport = () => {
    exportSegmentationDataToExcel(
      segments,
      selectableHeader1,
      selectableHeader2,
      driver1Type,
      driver2Type,
      driver1Mode,
      driver2Mode,
      driver1NumeratorType,
      driver2NumeratorType,
      tableName || "Segmentation",
      segments,
      data,
      sortColumn,
      sortDirection
    );
  };

  const handleSaveData = async () => {
    if (!data || data.length === 0) {
      return;
    }

    try {
      setIsLoading(true);
      await saveSegmentationData(data);
    } finally {
      setIsLoading(false);
    }
  };

  const shouldDisableCalcButton = validateDriversAndSegments({
    tableName,
    segmentGroups,
    count1Column: selectableHeader1,
    driver1Type,
    driver1Mode,
    driver1NumeratorType,
    driver2Type,
    driver2Mode,
    driver2NumeratorType,
  });

  const toolbarButtons = [
    {
      icon: FunctionsIcon,
      tooltip: "Calculate Segment Data",
      onClick: handleCalculateSegmentData,
      disabled: shouldDisableCalcButton,
    },
    {
      icon: SaveAltIcon,
      tooltip: "Save data",
      onClick: handleSaveData,
      disabled: !data || segmentationResult.loading,
    },
    {
      icon: DownloadIcon,
      tooltip: "Export to Excel",
      onClick: handleExport,
      disabled: !data,
    },
  ];

  if (loading) {
    return (
      <StyledTableContainer>
        <TableToolbar buttons={toolbarButtons} />
        <Table aria-label="skeleton table">
          <SegmentationTableHeader
            segments={segments}
            selectableHeader1={selectableHeader1}
            selectableHeader2={selectableHeader2}
            headerOptions={headerOptions}
            setSelectableHeader1={setSelectableHeader1}
            setSelectableHeader2={setSelectableHeader2}
            driver1Type={driver1Type}
            driver2Type={driver2Type}
            driver1Mode={driver1Mode}
            driver2Mode={driver2Mode}
            driver1NumeratorType={driver1NumeratorType}
            driver2NumeratorType={driver2NumeratorType}
            tableName={tableName}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            handleSort={handleSort}
            hasOneDataAndOneManualSegment={hasOneDataAndOneManualSegment}
            isSkeleton={true}
          />
          <SegmentationTableBody
            segments={segments}
            data={data}
            driver1Mode={driver1Mode}
            driver2Mode={driver2Mode}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            hasOneDataAndOneManualSegment={hasOneDataAndOneManualSegment}
            isSkeleton={true}
          />
        </Table>
      </StyledTableContainer>
    );
  }

  if (error) {
    return (
      <StyledTableContainer>
        <TableToolbar buttons={toolbarButtons} />
        <MDTypography variant="body1" color="error.main" align="center" p={2}>
          Error: {error.message}
        </MDTypography>
      </StyledTableContainer>
    );
  }

  return (
    <StyledTableContainer>
      <TableToolbar buttons={toolbarButtons} />
      <Table aria-label="segmentation table">
        <SegmentationTableHeader
          segments={segments}
          selectableHeader1={selectableHeader1}
          selectableHeader2={selectableHeader2}
          headerOptions={headerOptions}
          setSelectableHeader1={setSelectableHeader1}
          setSelectableHeader2={setSelectableHeader2}
          driver1Type={driver1Type}
          driver2Type={driver2Type}
          driver1Mode={driver1Mode}
          driver2Mode={driver2Mode}
          driver1NumeratorType={driver1NumeratorType}
          driver2NumeratorType={driver2NumeratorType}
          tableName={tableName}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          handleSort={handleSort}
          hasOneDataAndOneManualSegment={hasOneDataAndOneManualSegment}
          isSkeleton={false}
        />
        <SegmentationTableBody
          segments={segments}
          data={data}
          driver1Mode={driver1Mode}
          driver2Mode={driver2Mode}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          hasOneDataAndOneManualSegment={hasOneDataAndOneManualSegment}
          isSkeleton={false}
        />
      </Table>
      {!isManualOnly && (
        <TablePaginationWrapper
          totalCount={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      )}
    </StyledTableContainer>
  );
};

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(1),
  backgroundColor: colors.background.default,
  borderRadius: theme.spacing(1.5),
  boxShadow: theme.shadows[4],
  padding: theme.spacing(1),
  border: `1px solid ${colors.grey[300]}`,
  overflowX: "auto",
  maxHeight: "800px",
  overflowY: "auto",
}));

export default SegmentationTable;
